import {
  AccountDetailsResponse,
  AuthCredentials,
  GoogleLoginResponse,
  LoginResponse,
} from "../types/auth-types";
import { useHttpAdapter } from "./useHttpAdapter";
import { useCallback, useMemo } from "react";

type ForgotPasswordResponse = { status?: string; message?: string };

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.detail || error?.response?.data?.message || fallback;

export const useAuthApi = (showToast?: () => void) => {
  const { post, get } = useHttpAdapter();

  const fetchAccountDetails = useCallback(async () => {
    try {
      const response = await get<AccountDetailsResponse>("/fetchAccount");
      return response.status === 200 ? response.data : null;
    } catch (error) {
      console.error("Failed to fetch account details", error);
      showToast?.();
      return null;
    }
  }, [get, showToast]);

  const fetchMe = useCallback(async () => {
    try {
      const response = await get<AccountDetailsResponse>("/identity/me");
      return response.status === 200 ? response.data : null;
    } catch {
      return null;
    }
  }, [get]);

  const fetchAccount = useCallback(async () => {
    try {
      const accountDetails = await fetchAccountDetails();
      if (accountDetails?.data?.userData) {
        localStorage.setItem(
          "account_details",
          JSON.stringify(accountDetails.data.userData),
        );
      }
    } catch (error) {
      console.error("Failed to fetch account details during login", error);
    }
  }, [fetchAccountDetails]);

  const handleAccountLogin = useCallback(
    async (payload: AuthCredentials) => {
      const response = await post<LoginResponse>("/identity/login", payload);
      if (response.status === 200) {
        await fetchAccount();
        return response.data;
      }
      return null;
    },
    [post, fetchAccount],
  );

  const logout = useCallback(async () => {
    try {
      await post("/identity/logout");
    } catch {
      // best effort
    }
  }, [post]);

  const handleForgotPassword = useCallback(
    async (email: string) => {
      try {
        const response = await post<ForgotPasswordResponse>(
          "/identity/forgot-password",
          { email },
        );
        if (response.status === 200) {
          return { ok: true, message: response.data.message };
        }
        return { ok: false, message: "Failed to start password reset." };
      } catch (error: any) {
        return {
          ok: false,
          message: getErrorMessage(error, "Failed to start password reset."),
        };
      }
    },
    [post],
  );

  const handleGoogleSignIn = useCallback(async () => {
    try {
      const response = await get<GoogleLoginResponse>("/auth/login/google");
      if (response.status === 200 && response.data.auth_url) {
        console.log(response.data.auth_url);
        window.location.href = response.data.auth_url;
        return true;
      }
    } catch (error) {
      console.error("Google Sign In failed", error);
    }
    return false;
  }, [get]);

  return useMemo(
    () => ({
      handleAccountLogin,
      fetchAccountDetails,
      fetchAccount,
      fetchMe,
      logout,
      handleForgotPassword,
      handleGoogleSignIn,
    }),
    [
      handleAccountLogin,
      fetchAccountDetails,
      fetchAccount,
      fetchMe,
      logout,
      handleForgotPassword,
      handleGoogleSignIn,
    ],
  );
};
